import { makeStyles, tokens, Tooltip, Skeleton, SkeletonItem } from '@coreai/fluentui-react';
import { ConnectorIcon } from '@/components/ConnectionsView/ConnectorIcon';
import { useFeaturedConnector } from '@/hooks/useFeaturedConnector';

// =============================================================================
// STYLES
// =============================================================================

const CONTAINER_SIZE = '28px';

const useStyles = makeStyles({
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: CONTAINER_SIZE,
    height: CONTAINER_SIZE,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    flexShrink: 0,
  },
  overflowBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: CONTAINER_SIZE,
    height: CONTAINER_SIZE,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
});

// =============================================================================
// PROPS
// =============================================================================

export interface ConnectorIconRowProps {
  /** Connector ids, e.g. '/managedApis/office365' or 'connectionProviders/request'. */
  connectorIds: ReadonlyArray<string>;
  maxVisible?: number;
}

// =============================================================================
// COMPONENT
// =============================================================================

/** One tile per connector id — resolves name/icon at runtime via the hook. */
const FeaturedConnectorTile = ({ connectorId }: { connectorId: string }) => {
  const styles = useStyles();
  const { displayName, iconUri, isLoading } = useFeaturedConnector(connectorId);

  if (isLoading) {
    return (
      <div className={styles.iconContainer}>
        <Skeleton aria-label="Loading connector icon">
          <SkeletonItem shape="square" size={16} />
        </Skeleton>
      </div>
    );
  }

  // Icons are decorative — a failed metadata fetch just renders ConnectorIcon's
  // generic plug fallback with the derived name in the tooltip.
  return (
    <Tooltip content={displayName} relationship="label">
      <div className={styles.iconContainer}>
        <ConnectorIcon {...(iconUri ? { iconUri } : {})} size={16} />
      </div>
    </Tooltip>
  );
};

export const ConnectorIconRow = ({ connectorIds, maxVisible = 4 }: ConnectorIconRowProps) => {
  const styles = useStyles();

  if (connectorIds.length === 0) return null;

  const visible = connectorIds.slice(0, maxVisible);
  const overflow = connectorIds.length - maxVisible;

  return (
    <div className={styles.row}>
      {visible.map((connectorId) => (
        <FeaturedConnectorTile key={connectorId} connectorId={connectorId} />
      ))}
      {overflow > 0 && (
        <span className={styles.overflowBadge} aria-label={`${overflow} more connector${overflow !== 1 ? 's' : ''}`}>
          +{overflow}
        </span>
      )}
    </div>
  );
};
